<template>
    <b-container v-if="!ready">
        <b-row class="vh-100 text-center" align-v="center">
            <b-col>
                <b-spinner variant="light" />
            </b-col>
        </b-row>
    </b-container>
    <TemplateEditor v-model="nodes" @keyup="editorKeyup" :saving="saving" v-else >
        <template #nav>
            <b-nav-item-dropdown text="Files">
            <b-dropdown-item @click="save(false)">Save All</b-dropdown-item>
            <b-dropdown-item @click="save(true)">Save and Build</b-dropdown-item>
            </b-nav-item-dropdown>
        </template>
    </TemplateEditor>
</template>

<script>
const TemplateEditor = () => import(/* webpackChunkName: "template-editor" */ '../../components/editor/TemplateEditor')

export default {
    name: 'EditorV5',
    components: {
        TemplateEditor
    },

    props: {
        settings: {
            type: Object
        },

        app: {
            type: Object
        }
    },

    data: function() {
        return {
            appNodes: null,
            saving: false,
            ready: false,
            nodes: null
        }
    },

    computed: {
        scopeId() {
            return this.app.uuid.replace(/[0-9]/g, '').replaceAll('-', '');
        },

        version() {
            return this.$route.query.v
        }
    },

    methods: {
        save: _.debounce(async function(build) {
            this.saving = true;
            try {
                const nodes = JSON.parse(this.nodes[0].toString()).children

                await this.$http.put(`/v3/system/gateway/app-service/public-app/${this.app.uuid}/nodes`, {nodes, version: this.version, build})
            } catch (err) {
                this.$debugLog(err)

                if (err.response && err.response.data) {
                    this.$bvToast.toast(err.response.data.message, {
                        title: this.$t('error'),
                        variant: 'danger'
                    })

                    return
                }

                this.$bvToast.toast("Failed to save the app.", {
                    title: this.$t('error'),
                    variant: 'danger'
                })
            } finally {
                this.saving = false
            }
        }, 500),

        editorKeyup(e) {
            if (e.ctrlKey && e.code == 'KeyS') {
                this.save(false)
            }
        }
    },

    async mounted() {
        try {
            const { nodes = [] } = this.settings || {}
            this.appNodes = nodes

            let index = undefined;
            if (this.appNodes) {
                // check does templates have index.vue file if not add it programatically
                index = this.appNodes.find(n => {
                    return n.name.toLowerCase() == "index.vue" && n.ext == "vue"
                })
            }

            let routes = undefined;
            if (this.appNodes) {
                // check does templates have index.vue file if not add it programatically
                routes = this.appNodes.find(n => {
                    return n.name.toLowerCase() == "routes.js" && n.ext == "js"
                })
            }

            let config = undefined;
            if (this.appNodes) {
                // check does templates have index.vue file if not add it programatically
                config = this.appNodes.find(n => {
                    return n.name.toLowerCase() == "config.js" && n.ext == "js"
                })
            }

            let translation = undefined;
            if (this.appNodes) {
                // check does templates have index.vue file if not add it programatically
                translation = this.appNodes.find(n => {
                    return n.name.toLowerCase() == "i18n.js" && n.ext == "js"
                })
            }

            let store = undefined;
            if (this.appNodes) {
                // check does templates have index.vue file if not add it programatically
                store = this.appNodes.find(n => {
                    return n.name.toLowerCase() == "store.js" && n.ext == "js"
                })
            }

            let views = undefined;
            if (this.appNodes) {
                // check does templates have index.vue file if not add it programatically
                views = this.appNodes.find(n => {
                    return n.name.toLowerCase() == "views.json" && n.ext == "json"
                })
            }

            if (!index) {
                let indexNode = {
                    name: 'index.vue',
                    id: 3,
                    pid: 1,
                    isLeaf: true,
                    dragDisabled: false,
                    addTreeNodeDisabled: false,
                    addLeafNodeDisabled: false,
                    delNodeDisabled: true,
                    editNodeDisabled: true,
                    ext: 'vue',
                    path: 'index.vue',
                    children: null,
                    editorOptions: {
                        mode: 'text/x-vue'
                    },
                    content: `<template>
    <div> Hello </div>
</template>
`,
                }

                this.appNodes.push(indexNode)
            }

            if (!routes) {
                let routesNode = {
                    name: 'routes.js',
                    id: 4,
                    pid: 1,
                    isLeaf: true,
                    dragDisabled: false,
                    addTreeNodeDisabled: false,
                    addLeafNodeDisabled: false,
                    delNodeDisabled: true,
                    editNodeDisabled: true,
                    ext: 'js',
                    children: null,
                    path: 'routes.js',
                    editorOptions: {
                        mode: 'application/javascript'
                    },
                    content: `export default []
`,
                }

                this.appNodes.push(routesNode)
            }

            if (!config) {
                let configNode = {
                    name: 'config.js',
                    id: 5,
                    pid: 1,
                    isLeaf: true,
                    dragDisabled: false,
                    addTreeNodeDisabled: false,
                    addLeafNodeDisabled: false,
                    delNodeDisabled: true,
                    editNodeDisabled: true,
                    content: `export default {
    env: {
        live: {},
        dev: {}
    }
}
`,
                    ext: 'js',
                    children: null,
                    path: 'config.js',
                    editorOptions: {
                        mode: 'application/javascript'
                    }
                }

                this.appNodes.push(configNode)
            }

            if (!translation) {
                let configNode = {
                    name: 'i18n.js',
                    id: 6,
                    pid: 1,
                    isLeaf: true,
                    dragDisabled: false,
                    addTreeNodeDisabled: false,
                    addLeafNodeDisabled: false,
                    delNodeDisabled: true,
                    editNodeDisabled: true,
                    content: `
export default {
  locale: 'en',
  fallbackLocale: 'en',
  messages: {}
}
`,
                    ext: 'js',
                    children: null,
                    path: 'i18n.js',
                    editorOptions: {
                        mode: 'application/javascript'
                    }
                }

                this.appNodes.push(configNode)
            }

            if (!store) {
                let configNode = {
                    name: 'store.js',
                    id: 7,
                    pid: 1,
                    isLeaf: true,
                    dragDisabled: false,
                    addTreeNodeDisabled: false,
                    addLeafNodeDisabled: false,
                    delNodeDisabled: true,
                    editNodeDisabled: true,
                    content: `
export default {
    // list of modules
}
`,
                    ext: 'js',
                    children: null,
                    path: 'store.js',
                    editorOptions: {
                        mode: 'application/javascript'
                    }
                }

                this.appNodes.push(configNode)
            }

            if (!views) {
                let configNode = {
                    name: 'views.json',
                    id: 9,
                    pid: 1,
                    isLeaf: true,
                    dragDisabled: false,
                    addTreeNodeDisabled: false,
                    addLeafNodeDisabled: false,
                    delNodeDisabled: true,
                    editNodeDisabled: true,
                    content: `{
    "appSettings": null
}
`,
                    ext: 'json',
                    children: null,
                    path: 'views.json',
                    editorOptions: {
                        mode: 'application/json'
                    }
                }

                this.appNodes.push(configNode)
            }

            this.nodes = [
                {
                    name: this.app.name,
                    id: 1,
                    pid: 0,
                    root: true,
                    dragDisabled: false,
                    editNodeDisabled: true,
                    delNodeDisabled: true,
                    addTreeNodeDisabled: false,
                    addLeafNodeDisabled: false,
                    children: this.appNodes
                }
            ]

            this.ready = true
       }  catch(err) {
            this.$debugLog(err)
            this.$mainDialog.msgBoxOk("Failed to fetch component settings.")
        }
    }
}
</script>